import type {
  ApiUpsertPublicPageLocationDto,
  ApiUpsertPublicPageSocialLinkDto,
  ApiUpsertServiceOfferingDto,
  ContactChannelKind,
  PublicPageSectionKey,
  PublicPageSocialType,
  ServiceKey,
} from '@mfw/common';
import { UpsertPublicPageLocationDto } from '@mfw/common';

import type {
  ApiContactChannel,
  ApiPublicPage,
  ApiPublicPageLocation,
  ApiPublicPageSection,
  ApiServiceOffering,
  ApiSocialLink,
  PublicPageCurrency,
  PublicPageLocale,
  UiContactChannel,
  UiPublicPage,
  UiPublicPageLocation,
  UiPublicPageSection,
  UiServiceOffering,
  UiSocialLink,
  UiUpsertPublicPageLocation,
  UiUpsertServiceOffering,
  UiUpsertSocialLink,
} from '../../schemas/publicPage.schema';

export function mapPublicPageSectionFromApi(
  section: ApiPublicPageSection,
): UiPublicPageSection {
  return {
    id: section.id,
    key: section.key as PublicPageSectionKey,
    isVisible: section.isVisible,
    sortOrder: section.sortOrder,
    settings: section.settings,
  };
}

export function mapPublicPageFromApi(page: ApiPublicPage): UiPublicPage {
  return {
    id: page.id,
    organizationId: page.organizationId,
    userId: page.userId,
    isEnabled: page.isEnabled,
    isBlocked: page.isBlocked,
    slug: page.slug,
    locale: page.locale as PublicPageLocale,
    headline: page.headline,
    headlineTranslations: page.headlineTranslations ?? {},
    intro: page.intro,
    sections: page.sections.map(mapPublicPageSectionFromApi),
    createdAt: page.createdAt,
    updatedAt: page.updatedAt,
  };
}

export function mapContactChannelFromApi(
  channel: ApiContactChannel,
): UiContactChannel {
  return {
    id: channel.id,
    kind: channel.kind as ContactChannelKind,
    value: channel.value,
    label: channel.label,
    isPublic: channel.isPublic,
    sortOrder: channel.sortOrder,
  };
}

export function mapServiceOfferingFromApi(
  offering: ApiServiceOffering,
): UiServiceOffering {
  return {
    id: offering.id,
    serviceKey: offering.serviceKey as ServiceKey,
    title: offering.title ?? null,
    description: offering.description ?? null,
    priceFrom: offering.priceFrom,
    priceTo: offering.priceTo,
    currency: offering.currency as PublicPageCurrency,
    unit: offering.unit,
    note: offering.note,
    isPublic: offering.isPublic,
    sortOrder: offering.sortOrder,
  };
}

/**
 * Fix round 1/5, Finding 3 (Important): this used to convert `null ->
 * undefined`, which is exactly wrong for a FULL-REPLACE PATCH. `undefined`
 * is dropped by `JSON.stringify` (axios), so the key never reached the
 * request body at all; the BE DTO's `.nullish()` happily validated the
 * absent key; and `updateOffering` does `offering.update(parsed)` — a
 * partial Sequelize update that leaves the column untouched. Net effect:
 * clearing a price, saving, getting a 200 back — and the old price still
 * sitting in the database. `null` is sent AS `null` now: the RHF-resurrection
 * bug this project's "number-field-clear-bug" convention guards against is
 * about what the FORM FIELD holds (`undefined` there means "no value,
 * restore the mount value"), not what the wire payload should say — and the
 * wire payload for a full-replace PATCH must say `null`, explicitly, to mean
 * "cleared." `unit`/`note` were already correct (plain text fields, no
 * RHF-undefined bug, already sent as honest `null`) — this fixes the two
 * numeric fields to match.
 */
export function mapUpsertServiceOfferingToApi(
  data: UiUpsertServiceOffering,
): ApiUpsertServiceOfferingDto {
  return {
    serviceKey: data.serviceKey,
    priceFrom: data.priceFrom,
    priceTo: data.priceTo,
    currency: data.currency,
    unit: data.unit,
    note: data.note,
    isPublic: data.isPublic,
    sortOrder: data.sortOrder,
  };
}

export function mapPublicPageLocationFromApi(
  location: ApiPublicPageLocation,
): UiPublicPageLocation {
  return {
    id: location.id,
    label: location.label,
    street: location.street,
    city: location.city,
    state: location.state,
    country: location.country,
    zip: location.zip,
    isAddressPublic: location.isAddressPublic,
    sortOrder: location.sortOrder,
  };
}

// The editor converts empty inputs to null. Use the shared write schema
// at the API boundary so a legacy free-text country cannot be sent as ISO.
export function mapUpsertPublicPageLocationToApi(
  data: UiUpsertPublicPageLocation,
): ApiUpsertPublicPageLocationDto {
  return UpsertPublicPageLocationDto.parse({
    label: data.label,
    street: data.street,
    city: data.city,
    state: data.state,
    country: data.country,
    zip: data.zip,
    isAddressPublic: data.isAddressPublic,
    sortOrder: data.sortOrder,
  });
}

export function mapSocialLinkFromApi(link: ApiSocialLink): UiSocialLink {
  return {
    id: link.id,
    type: link.type as PublicPageSocialType,
    url: link.url,
    isPublic: link.isPublic,
    sortOrder: link.sortOrder,
  };
}

export function mapUpsertSocialLinkToApi(
  data: UiUpsertSocialLink,
): ApiUpsertPublicPageSocialLinkDto {
  return {
    type: data.type,
    url: data.url,
    isPublic: data.isPublic,
    sortOrder: data.sortOrder,
  };
}
